/**
 * useReferralCapture — captures ?ref= from the URL, persists to localStorage,
 * and fires POST /referral/track so clicks are recorded even before signup.
 *
 * Mount this ONCE at the App root (inside BrowserRouter so useLocation works).
 * It is safe to call on every route — it only fires once per referral code visit.
 */

import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { ReferralService } from '../services/referral/referralService';

const REF_CODE_KEY = 'tt_ref_code';
const REF_TRACKED_KEY = 'tt_ref_tracked'; // "code:timestamp" of last tracked visit

/**
 * Read the stored referral code (if any). Used by the signup flow to
 * call /referral/attribute after successful registration.
 */
export function getStoredReferralCode(): string | null {
  try {
    return localStorage.getItem(REF_CODE_KEY);
  } catch {
    return null;
  }
}

/**
 * Clear the stored referral code after attribution succeeds.
 */
export function clearStoredReferralCode(): void {
  try {
    localStorage.removeItem(REF_CODE_KEY);
    localStorage.removeItem(REF_TRACKED_KEY);
  } catch {
    // ignore
  }
}

export function useReferralCapture(): void {
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const code = params.get('ref')?.trim();
    if (!code) return;

    // Persist the code so it survives login/redirect flows
    try {
      localStorage.setItem(REF_CODE_KEY, code);
    } catch {
      // storage full or blocked — can't track
      return;
    }

    // Only fire track once per code (avoid duplicate clicks on reload)
    const tracked = localStorage.getItem(REF_TRACKED_KEY) ?? '';
    if (tracked.startsWith(`${code}:`)) return;

    // Fire and forget — don't block rendering
    const svc = ReferralService.getInstance();
    svc
      .trackVisit(code, {
        referrerUrl: document.referrer || undefined,
        utmSource: params.get('utm_source') || undefined,
        utmMedium: params.get('utm_medium') || undefined,
        utmCampaign: params.get('utm_campaign') || undefined,
      })
      .then((res) => {
        if (res.accepted) {
          localStorage.setItem(REF_TRACKED_KEY, `${code}:${Date.now()}`);
          console.log('[referral] click tracked:', code);
        } else {
          console.warn('[referral] track rejected:', res.reason);
        }
      })
      .catch((err) => {
        console.warn('[referral] track error:', err);
      });
  }, [location.search]);
}
